import { LOADING_STATUSES } from "constants/loadingStatuses"

import { useCallback, useEffect } from "react"

import styled from 'styled-components'
import { PageTopbar } from "features/common/page-topbar"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from  "store"
import {
  loadTimelineItemsFirstTimeThunk,
  subscribeTimelineToWSThunk,
  TIMELINE_FILTERS,
  timelineActions,
  unsubscribeTimelineFromWSThunk
} from "features/photo/timeline/timelineSlice"
import { getTimelineLoadingStatusSelector,getTimelineSelectedItemsCountSelector, getTimelineSelectedItemsSelector, getSortFormatSelector } from "features/photo/timeline/selectors"
import { useNavigate } from "react-router-dom"
import { SDK_TYPES } from "sdk/sdkConstants"
import { PageTimelineSkeleton, STYLED_VARIABLES } from "@cloudike/web_ui_components"
import { useMobileDetection } from "features/common/hooks"
import { usePageTitle } from "features/common/hooks/usePageTitle"
import { useCardsPerRow } from "features/photo/timeline/useCardsPerRow"

import { Timeline } from "../../../features/photo/timeline/Timeline"
import { addSelectedToFavoritesItemsADDThunk } from "../../../features/favorites/favoritesSlice"
import { appActions } from "../../../store/app"
import { analytics,ANALYTICS_EVENTS } from "../../../features/common/analytics"

interface AddingFavoriteToAlbumPageProps {
  type: SDK_TYPES
}

const AddingPhotosToFavorite: React.FC<AddingFavoriteToAlbumPageProps> = ({ type }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedItems = getTimelineSelectedItemsSelector()
  const selectedItemsCount = getTimelineSelectedItemsCountSelector()
  const status = getTimelineLoadingStatusSelector()
  const isMobile = useMobileDetection()

  const sortFormat = getSortFormatSelector()
  const cardsPerRow = useCardsPerRow(sortFormat)

  usePageTitle('l_common_addToFavorite')

  useEffect(() => {
    dispatch(appActions.setIsMainContainerWithFullWidth(true))
    dispatch(timelineActions.setCurrentTimelineType(type))
    dispatch(loadTimelineItemsFirstTimeThunk({ cardsPerRow }))
    dispatch(subscribeTimelineToWSThunk())

    return () => {
      dispatch(appActions.setIsMainContainerWithFullWidth(false))
      dispatch(unsubscribeTimelineFromWSThunk())
      dispatch(timelineActions.resetState())
    }
  }, [])

  const handleCancelSelection = useCallback(() => {
    dispatch(timelineActions.unselectAll())
  }, [])

  const handleBack = () => {
    dispatch(timelineActions.setFilter(TIMELINE_FILTERS.favorites))
    navigate('/photos')
  }

  const handleAddPhotos = () => {
    const callback = () => {
      dispatch(timelineActions.setFilter(TIMELINE_FILTERS.favorites))
      navigate('/photos')
    }

    dispatch(addSelectedToFavoritesItemsADDThunk({ items: selectedItems, callback, type }))
    dispatch(appActions.toggleRightMenuOnMobile(false))
    analytics.push(ANALYTICS_EVENTS.WEB_PHOTOS_ACTIONS_ADD_TO_FAVORITES)
  }

  return (
    <>
      <PageTopbarBox
        title={t('l_common_addToFavorite')}
        selectedText={isMobile ? t('l_common_selectedObjects', { number: selectedItemsCount }) : t('l_files_selectedFiles', { number: selectedItemsCount })}
        selectedItemsCount={selectedItemsCount}
        onCancelSelection={handleCancelSelection}
        description={!isMobile ? t('l_common_addToFavoriteMessage') : ''}
        onAccept={isMobile && handleAddPhotos}
        isAcceptBtnActive={isMobile && !!selectedItems.length}
        onBack={handleBack}
        isMobile={isMobile}
      />

      {
        status === LOADING_STATUSES.LOADING &&
        <StyledSceleton />
      }

      {
        status !== LOADING_STATUSES.LOADING && (
          <TimelineBox
            type={type}
            withDisabledPreview
            withDisabledPhotoCard
            isTimelinePage={true}
            isAddToFavoritesPage={true}
            cardsPerRow={cardsPerRow}
          />
        )
      }
    </>
  )
}

const StyledSceleton = styled(PageTimelineSkeleton)`
  padding: 0;
`

const PageTopbarBox = styled(PageTopbar)`
  background: var(--background-primary);
  
  @media (max-width: ${STYLED_VARIABLES.BREAKPOINTS.PHONE_90}) {
    padding-bottom: 15px;
  }
`

const TimelineBox = styled(Timeline)`
`

export default AddingPhotosToFavorite
